import { Component, h, Prop, Element } from "@stencil/core";
import lottie, { AnimationItem } from "lottie-web";
import { Controller, Scene } from "scrollmagic";

import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";
import { prefersReducedMotion } from "../../utilities/user";
import { parseTrackingAttributes } from "../../utilities/card";

interface ScrollOnPlaceCard {
  preTitle?: string;
  title: string;
  description?: string;
  linkHref?: string;
  linkLabel?: string; // optional custom label
  imageSrc: string; // desktop image (or generic)
  imageMobileSrc?: string; // mobile override
  imgType: "image" | "video" | "lottie";
  // Advanced formats
  lottieSrc?: string; // .json / .lot path
  brightcoveVideoId?: string; // placeholder support
  trackingEnabled?: boolean;
  trackingAttributes?: string;
}

@Component({
  tag: "scs-scroll-on-place",
  styleUrls: ["scroll-on-place.scss"],
  shadow: true,
})
export class ScrollOnPlace {
  @Element() hostEl!: HTMLElement;

  /** JSON encoded array (max 4) of cards */
  @Prop() cards: string = "[]";
  /** Image side on desktop */
  @Prop() imagePosition: "left" | "right" = "left";
  @Prop()
  mainTitleVisualLevel:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "d1"
    | "d2"
    | "d3" = "d2";

  private cardsData: ScrollOnPlaceCard[] = [];
  private controller: Controller | undefined;
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  private readonly trackByEvent: (event: Event) => void;

  private animationsToCleanupOnDestroy: AnimationItem[] = [];

  constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
  }

  componentWillLoad() {
    this.parseCards();
  }

  createScene = (
    controller: Controller,
    triggerElement: Element,
    pin: Element,
    leadingElementForMobile: Element,
    duration: number,
    offset: number,
    triggerHook: number,
    isFirstElement = false,
    isLastElement = false,
    previousElement: Element | null = null,
  ) => {
    const scene = new Scene({
      triggerElement,
      duration: duration,
      offset: offset,
      triggerHook: triggerHook,
    }).setPin(pin);

    // Fade logic: element becomes visible on enter, starts fading near end of scene.
    // const fadeStart = 0.75; // progress threshold at which fade-out begins
    const fadeStart = 1; // progress threshold at which fade-out begins
    if (this.isDesktop()) {
      scene.on("enter", () => {
        (pin as HTMLElement).classList.add("visible");
        (triggerElement as HTMLElement).classList.add("active");
        (pin as HTMLElement).classList.remove("fade-out");
      });
      scene.on("progress", (event: any) => {
        // Only trigger fade-out while scrolling forward; remove during reverse so it stays visible.
        if (event.scrollDirection === "FORWARD") {
          if (event.progress >= fadeStart) {
            (pin as HTMLElement).classList.add("fade-out");
          } else {
            (pin as HTMLElement).classList.remove("fade-out");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (pin as HTMLElement).classList.remove("fade-out");
        }
      });
      scene.on("leave", (event: any) => {
        // If we are leaving because we've scrolled past the end going forward, hide.
        // If we are leaving in reverse (going back to BEFORE), keep it visible so the first image stays shown at top.
        if (event.scrollDirection === "FORWARD") {
          if (isLastElement) {
            (triggerElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (triggerElement as HTMLElement).classList.remove("active");
            (pin as HTMLElement).classList.remove("visible");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (triggerElement as HTMLElement).classList.remove("active");
          (pin as HTMLElement).classList.remove("visible");
          if (isFirstElement) {
            (pin as HTMLElement).classList.add("visible");
            (triggerElement as HTMLElement).classList.add("active");
          } else {
            (pin as HTMLElement).classList.remove("visible");
            (triggerElement as HTMLElement).classList.remove("active");
          }
        }
        (pin as HTMLElement).classList.remove("fade-out");
      });
    } else {
      // Mobile
      scene.on("enter", (event: any) => {
        if (event.scrollDirection === "FORWARD") {
          leadingElementForMobile.classList.add("add-spacer");
        }
        (pin as HTMLElement).classList.add("visible");
        (triggerElement as HTMLElement).classList.add("active");
        (pin as HTMLElement).classList.remove("fade-out");
        previousElement?.classList?.remove("visible");
        previousElement?.classList?.add("fade-out");
      });
      scene.on("progress", (event: any) => {
        // Only trigger fade-out while scrolling forward; remove during reverse so it stays visible.
        if (event.scrollDirection === "FORWARD") {
          if (event.progress >= fadeStart) {
            (pin as HTMLElement).classList.add("fade-out");
          } else {
            (pin as HTMLElement).classList.remove("fade-out");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (pin as HTMLElement).classList.remove("fade-out");
        }
      });
      scene.on("leave", (event: any) => {
        // If we are leaving because we've scrolled past the end going forward, hide.
        // If we are leaving in reverse (going back to BEFORE), keep it visible so the first image stays shown at top.
        if (event.scrollDirection === "FORWARD") {
          if (isLastElement) {
            (triggerElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (triggerElement as HTMLElement).classList.remove("active");
            (pin as HTMLElement).classList.remove("visible");
          }
        } else if (event.scrollDirection === "REVERSE") {
          if (isFirstElement) {
            (triggerElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (pin as HTMLElement).classList.remove("visible");
            (triggerElement as HTMLElement).classList.remove("active");
          }
        }
        (pin as HTMLElement).classList.remove("fade-out");
        if (event.scrollDirection === "REVERSE") {
          pin.querySelector(".leading-spacer")?.classList.remove("add-spacer");
        }
      });
    }

    scene.addTo(controller);
    return scene;
  };

  setImageWidth(isDesktop: boolean) {
    const imgWidth =
      this.hostEl.shadowRoot?.querySelector(".cards")?.clientWidth;
    if (!imgWidth) {
      console.log("Couldn't check image width for scroll-on-place element.");
      return;
    }
    const imgs = [
      ...(this.hostEl.shadowRoot?.querySelectorAll(".card-image-container") ??
        []),
    ];
    imgs.forEach(img => {
      (img as HTMLElement).style.width = isDesktop
        ? imgWidth * 0.55 + "px"
        : imgWidth + "px";
    });
  }

  componentDidLoad() {
    this.controller = new Controller();
    const sectionElements = [
      ...(this.hostEl.shadowRoot?.querySelectorAll(".trigger") ?? []),
    ];

    for (let i = 0; i < sectionElements.length; i++) {
      const isFirstElement = i === 0;
      const isLastElement = i === sectionElements.length - 1;
      const currentElement = sectionElements[i];
      const pin = currentElement?.querySelector(".card-image-container");
      const leadingElementForMobile = pin!.querySelector(".leading-spacer")!;
      this.setImageWidth(this.isDesktop());

      if (currentElement && pin) {
        const lottieElem = currentElement.querySelector(".lottie-dottie");
        if (lottieElem) {
          const lottieAnimation = lottie.loadAnimation({
            container: lottieElem,
            renderer: "svg",
            loop: true,
            autoplay: !prefersReducedMotion(),
            animationData: JSON.parse(this.cardsData[i]?.imageSrc || "{}"),
          });

          this.animationsToCleanupOnDestroy.push(lottieAnimation);
        }

        if (this.isDesktop()) {
          const offset = 0;
          const duration = isLastElement
            ? 300
            : (currentElement as any).offsetHeight;
          const triggerHook =
            window.innerWidth > 1280
              ? 0.2
              : window.innerWidth > 1024
                ? 0.3
                : 0.35;

          this.createScene(
            this.controller,
            currentElement,
            pin,
            leadingElementForMobile,
            duration,
            offset,
            triggerHook,
            isFirstElement,
            isLastElement,
            sectionElements[i - 1] ?? null,
          );
        } else {
          // Mobile
          const offset = 0;
          const duration = (currentElement as any).clientHeight;
          const triggerHook = 0.15;
          this.createScene(
            this.controller,
            currentElement,
            pin,
            leadingElementForMobile,
            duration,
            offset,
            triggerHook,
            isFirstElement,
            isLastElement,
          );
        }
      }
    }

    // Ensure first image is visible immediately (in case progress events haven't fired yet)
    const firstElement = sectionElements[0];
    const firstPin = firstElement?.querySelector(".card-image-container");
    if (firstPin) {
      firstPin.classList.add("visible");
      (firstElement as HTMLElement).classList.add("active");
    }
  }

  disconnectedCallback() {
    this.controller?.destroy(true);

    this.animationsToCleanupOnDestroy.forEach(animation => animation.destroy());
    this.animationsToCleanupOnDestroy = [];
  }

  private parseCards() {
    try {
      const arr = JSON.parse(this.cards || "[]") as ScrollOnPlaceCard[];
      if (Array.isArray(arr)) {
        this.cardsData = arr
          .filter(c => c && c.title && c.imageSrc)
          .map(cd => {
            if (!cd.imgType) {
              cd.imgType = "image";
            }
            return cd;
          })
          .slice(0, 4);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("Error parsing cards data:", e);
      this.cardsData = [];
    }
  }

  private isDesktop() {
    return window.innerWidth >= 768; // align with project breakpoint usage
  }

  private getImageSrcForCard(card: ScrollOnPlaceCard) {
    if (this.isDesktop()) {
      return card.imageSrc;
    }
    return card.imageMobileSrc || card.imageSrc;
  }

  public render() {
    if (!this.cardsData.length) {
      return <div class="sop-wrapper sop-empty">No cards configured</div>;
    }
    return (
      <ul
        class={`cards ${this.imagePosition === "right" ? "image-right" : "image-left"}`}
      >
        {this.cardsData.map((card, i) => (
          <li
            key={i}
            class="card-container trigger"
            id={`scrollmagic-trigger-${i}`}
            style={
              this.imagePosition === "right" && this.isDesktop()
                ? { flexDirection: "row-reverse" }
                : {}
            }
          >
            <div class="card-image-container">
              <div class="leading-spacer" aria-hidden="true"></div>
              {card.imgType === "image" && (
                <img
                  class="card-image"
                  src={this.getImageSrcForCard(card)}
                  id={`${i}`}
                  alt={card.title}
                />
              )}
              {card.imgType === "video" && (
                <video
                  class="card-image"
                  muted
                  playsInline
                  loop
                  autoPlay={!prefersReducedMotion()}
                >
                  <source
                    src={this.getImageSrcForCard(card)}
                    type="video/mp4"
                  />
                </video>
              )}
              {card.imgType === "lottie" && (
                <figure class="card-image lottie-dottie" />
              )}
            </div>
            <div class="card-text-content">
              {card.preTitle && (
                <h3 class="h4 font--semi-light">{card.preTitle}</h3>
              )}
              <h2 class={`${this.mainTitleVisualLevel} card-title`}>
                {card.title}
              </h2>
              {card.description && (
                <p class="card-description">{card.description}</p>
              )}
              {card.linkHref && card.linkLabel && (
                <a
                  href={card.linkHref}
                  class="link link--standalone"
                  data-track-action={card.trackingEnabled && "link"}
                  data-track-index={i}
                  data-track-link={card.linkHref}
                  {...parseTrackingAttributes(card.trackingAttributes || "{}")}
                  onClick={(ev: Event) => {
                    if (card.trackingEnabled) {
                      this.trackByEvent(ev);
                    }
                  }}
                >
                  {card.linkLabel}
                </a>
              )}
            </div>
          </li>
        ))}
        <li class="trailing-spacer" aria-hidden="true"></li>
      </ul>
    );
  }
}
